/**
 * Detail pipeline for work-log rows, merged from upstream t3code session-logic
 * (ticket 3522, upstream pin e3e7cc3f, MIT).
 *
 * Two behaviors arrive here:
 *
 * - Command output: a command row's detail is the command's output, not an
 *   echo of the command. Codex aggregated output, Claude raw output summaries,
 *   and OpenCode detail-only output each get their own extraction path.
 * - Runtime diagnostics: a runtime.error or runtime.warning activity keeps the
 *   provider's retained message in its detail, beyond the (often truncated)
 *   summary, so the row can show what actually went wrong.
 *
 * The command preview comes from workLogCommand.ts. The two upstream
 * presentation helpers the pipeline consults (`extractCommandOutputText`,
 * `commandDetailRepeatsCommand`) are byte-faithful copies in
 * workLogPresentation.ts, from upstream
 * `packages/client-runtime/src/work-log/presentation.ts` at the pin above.
 *
 * @module workLogDetail
 */
import {
  isToolLifecycleItemType,
  type OrchestrationThreadActivity,
  type ToolLifecycleItemType,
} from "@cmmd-forge/contracts";

import { extractToolCommand, stripTrailingExitCode } from "./workLogCommand";
import { commandDetailRepeatsCommand, extractCommandOutputText } from "./workLogPresentation";

/** A row with nothing a user can act on does not render (upstream's rule). */
export function isNoContentRuntimeWarning(
  activity: Pick<OrchestrationThreadActivity, "kind" | "summary">,
): boolean {
  return (
    activity.kind === "runtime.warning" &&
    activity.summary.endsWith("(no displayable text content)")
  );
}

/**
 * Runtime diagnostics keep the retained provider message in `detail` when it
 * adds anything beyond the summary. The summary is ingestion-truncated, so the
 * full message often only exists here.
 */
export function runtimeDiagnosticDetail(
  payload: Record<string, unknown> | null,
  summary: string,
): string | null {
  const message = asTrimmedString(payload?.message);
  if (
    message &&
    normalizePreviewForComparison(message) !== normalizePreviewForComparison(summary)
  ) {
    return message;
  }
  return null;
}

export function extractWorkLogItemType(
  payload: Record<string, unknown> | null,
): ToolLifecycleItemType | undefined {
  if (typeof payload?.itemType === "string" && isToolLifecycleItemType(payload.itemType)) {
    return payload.itemType;
  }
  return undefined;
}

function normalizeInlinePreview(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function truncateInlinePreview(value: string, maxLength = 84): string {
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, maxLength - 1).trimEnd()}…`;
}

function normalizePreviewForComparison(value: string | null | undefined): string | null {
  const normalized = asTrimmedString(value);
  if (!normalized) {
    return null;
  }
  return normalizeCompactToolLabel(normalizeInlinePreview(normalized)).toLowerCase();
}

function summarizeToolTextOutput(value: string): string | null {
  const lines: Array<string> = [];
  for (const rawLine of value.split(/\r?\n/u)) {
    const line = normalizeInlinePreview(rawLine);
    if (line.length > 0) {
      lines.push(line);
    }
  }
  const firstLine = lines.find((line) => line !== "```");
  if (firstLine) {
    return truncateInlinePreview(firstLine);
  }
  if (lines.length > 1) {
    return `${lines.length.toLocaleString()} lines`;
  }
  return null;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function summarizeToolRawOutput(payload: Record<string, unknown> | null): string | null {
  const data = asRecord(payload?.data);
  const rawOutput = asRecord(data?.rawOutput);
  if (!rawOutput) {
    return null;
  }

  const totalFiles = asNumber(rawOutput.totalFiles);
  if (totalFiles !== null) {
    const suffix = rawOutput.truncated === true ? "+" : "";
    return `${totalFiles.toLocaleString()} file${totalFiles === 1 ? "" : "s"}${suffix}`;
  }

  const content = asTrimmedString(rawOutput.content);
  if (content) {
    return summarizeToolTextOutput(content);
  }

  const stdout = asTrimmedString(rawOutput.stdout);
  if (stdout) {
    return summarizeToolTextOutput(stdout);
  }

  return null;
}

function extractToolOutput(payload: Record<string, unknown> | null): string | null {
  const output = extractCommandOutputText(payload?.data);
  return output ? stripTrailingExitCode(output).output : null;
}

function isCommandToolDetail(payload: Record<string, unknown> | null, heading: string): boolean {
  const data = asRecord(payload?.data);
  const kind = asTrimmedString(data?.kind)?.toLowerCase();
  const title = asTrimmedString(payload?.title ?? heading)?.toLowerCase();
  return (
    extractWorkLogItemType(payload) === "command_execution" ||
    kind === "execute" ||
    title === "terminal" ||
    title === "ran command"
  );
}

/**
 * The detail a work-log row shows for a non-task activity. Command rows show
 * their output; a detail that only repeats the command is dropped; a raw
 * output summary fills in when the payload carries no detail of its own.
 */
export function extractToolDetail(
  payload: Record<string, unknown> | null,
  heading: string,
): string | null {
  const rawDetail = asTrimmedString(payload?.detail);
  const detail = rawDetail ? stripTrailingExitCode(rawDetail).output : null;
  const normalizedHeading = normalizePreviewForComparison(heading);
  const normalizedDetail = normalizePreviewForComparison(detail);
  const commandTool = isCommandToolDetail(payload, heading);
  const commandPreview = commandTool
    ? extractToolCommand(payload)
    : { command: null, rawCommand: null };
  const command = commandPreview.command;

  if (commandTool && command) {
    const output = extractToolOutput(payload);
    if (output) return output;
  }

  const data = asRecord(payload?.data);
  const repeatsCommand =
    detail !== null &&
    commandDetailRepeatsCommand({
      detail,
      command,
      rawCommand: commandPreview.rawCommand,
      toolName: data?.toolName,
      data,
    });

  if (detail && normalizedHeading !== normalizedDetail && (!commandTool || !repeatsCommand)) {
    return detail;
  }

  if (commandTool) {
    return null;
  }

  const rawOutputSummary = summarizeToolRawOutput(payload);
  if (rawOutputSummary) {
    const normalizedRawOutputSummary = normalizePreviewForComparison(rawOutputSummary);
    if (normalizedRawOutputSummary !== normalizedHeading) {
      return rawOutputSummary;
    }
  }

  return null;
}

export function normalizeCompactToolLabel(value: string): string {
  return value.replace(/\s+(?:complete|completed)\s*$/i, "").trim();
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function asTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
